import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { 
  Bus, MapPin, Clock, Users, Wifi, Wind, Usb, Bath, 
  ArrowRight, Calendar, AlertCircle, ChevronLeft, ChevronRight,
  Sun, Moon, Sparkles, CheckCircle2, Info
} from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, addDays, subDays, startOfDay } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getTripDateDetails = (departureIso, arrivalIso) => {
  if (!departureIso) {
    return {
      depTime: '',
      depDateFull: '',
      depDateShort: '',
      depDateKey: '',
      isToday: false,
      isTomorrow: false,
      arrTime: '',
      arrDateFull: '',
      arrDateShort: '',
      dayDiff: 0
    };
  }

  try {
    const dep = parseISO(departureIso);
    const arr = arrivalIso ? parseISO(arrivalIso) : null;
    const depToday = isToday(dep);
    const depTomorrow = isTomorrow(dep);

    let dayDiff = 0;
    if (arr) {
      const depMidnight = startOfDay(dep);
      const arrMidnight = startOfDay(arr);
      dayDiff = Math.round((arrMidnight.getTime() - depMidnight.getTime()) / (1000 * 60 * 60 * 24));
    }

    return {
      depDateObj: dep,
      depDateKey: format(dep, 'yyyy-MM-dd'),
      depTime: format(dep, 'HH:mm'),
      depDateFull: format(dep, 'EEEE, dd MMMM yyyy'),
      depDateShort: format(dep, 'EEE, dd MMM yyyy'),
      depDayOfWeek: format(dep, 'EEEE'),
      isToday: depToday,
      isTomorrow: depTomorrow,
      arrTime: arr ? format(arr, 'HH:mm') : null,
      arrDateFull: arr ? format(arr, 'EEEE, dd MMMM yyyy') : null,
      arrDateShort: arr ? format(arr, 'EEE, dd MMM yyyy') : null,
      dayDiff
    };
  } catch (err) {
    return {
      depDateKey: departureIso.slice(0, 10),
      depTime: departureIso.slice(11, 16),
      depDateFull: departureIso.slice(0, 10),
      depDateShort: departureIso.slice(0, 10),
      isToday: false,
      isTomorrow: false,
      arrTime: arrivalIso ? arrivalIso.slice(11, 16) : null,
      arrDateFull: arrivalIso ? arrivalIso.slice(0, 10) : null,
      arrDateShort: arrivalIso ? arrivalIso.slice(0, 10) : null,
      dayDiff: 0
    };
  }
};

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('USD');
  const [originName, setOriginName] = useState('');
  const [destinationName, setDestinationName] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('selected'); // 'selected', 'tomorrow', 'all'

  const origin = searchParams.get('origin') || '';
  const destination = searchParams.get('destination') || '';
  const searchedDateParam = searchParams.get('date') || format(new Date(), 'yyyy-MM-dd');
  const passengers = parseInt(searchParams.get('passengers') || '1');

  useEffect(() => {
    searchTrips();
    fetchStationNames();
  }, [origin, destination, searchedDateParam, passengers]);

  const fetchStationNames = async () => {
    try {
      const response = await axios.get(`${API_URL}/stations`);
      const stations = response.data;
      const originStation = stations.find(s => s.id === origin);
      const destStation = stations.find(s => s.id === destination);
      setOriginName(originStation ? originStation.name : origin);
      setDestinationName(destStation ? destStation.name : destination);
    } catch (error) {
      setOriginName(origin);
      setDestinationName(destination);
    }
  };

  const searchTrips = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/search/trips`, {
        origin,
        destination,
        travel_date: searchedDateParam,
        passengers
      });
      setTrips(response.data.trips || []);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Failed to search trips');
    } finally {
      setLoading(false);
    }
  };

  const getAmenityIcon = (amenity) => {
    const icons = {
      'WiFi': Wifi,
      'AC': Wind,
      'USB Charging': Usb,
      'Toilet': Bath,
    };
    return icons[amenity] || null;
  };

  const handleSelectTrip = (tripId) => {
    navigate(`/select-seats/${tripId}?passengers=${passengers}&currency=${currency}&origin=${encodeURIComponent(originName || origin)}&destination=${encodeURIComponent(destinationName || destination)}`);
  };

  const handleDateChange = (newDateStr) => {
    const params = new URLSearchParams({
      origin,
      destination,
      date: newDateStr,
      passengers: passengers.toString()
    });
    setSelectedTab('selected');
    navigate(`/search?${params.toString()}`);
  };

  const searchedDateObj = useMemo(() => {
    try {
      return parseISO(searchedDateParam);
    } catch (e) {
      return new Date();
    }
  }, [searchedDateParam]);

  const tomorrowDateObj = useMemo(() => {
    return addDays(new Date(), 1);
  }, []);

  const tomorrowDateStr = useMemo(() => {
    return format(tomorrowDateObj, 'yyyy-MM-dd');
  }, [tomorrowDateObj]);

  const todayDateStr = useMemo(() => {
    return format(new Date(), 'yyyy-MM-dd');
  }, []);

  // Enrich trips with parsed date details
  const enrichedTrips = useMemo(() => {
    return trips.map(t => ({
      ...t,
      dateInfo: getTripDateDetails(t.departure, t.arrival)
    }));
  }, [trips]);

  // Groupings and counts
  const tripsOnSearchedDate = useMemo(() => {
    return enrichedTrips.filter(t => t.dateInfo.depDateKey === searchedDateParam);
  }, [enrichedTrips, searchedDateParam]);

  const tripsTomorrow = useMemo(() => {
    return enrichedTrips.filter(t => t.dateInfo.depDateKey === tomorrowDateStr);
  }, [enrichedTrips, tomorrowDateStr]);

  // Determine which trips to display based on selected tab
  const displayedTrips = useMemo(() => {
    if (selectedTab === 'selected') {
      return tripsOnSearchedDate.length > 0 ? tripsOnSearchedDate : enrichedTrips;
    }
    if (selectedTab === 'tomorrow') {
      return tripsTomorrow;
    }
    return enrichedTrips;
  }, [selectedTab, tripsOnSearchedDate, tripsTomorrow, enrichedTrips]);

  const isSearchedDateToday = searchedDateParam === todayDateStr;
  const isSearchedDateTomorrow = searchedDateParam === tomorrowDateStr;

  const canGoPrevious = useMemo(() => {
    const startOfCurrent = startOfDay(new Date());
    const startOfSearched = startOfDay(searchedDateObj);
    return startOfSearched > startOfCurrent;
  }, [searchedDateObj]);

  return (
    <PublicLayout>
      {/* Header Banner */}
      <div className="bg-panashe-blue py-8 border-b-4 border-panashe-gold">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-between gap-4 text-white">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
                <MapPin className="h-5 w-5 text-panashe-gold" />
                <span className="font-heading text-lg sm:text-xl font-bold">{originName || origin}</span>
              </div>
              <ArrowRight className="h-5 w-5 text-panashe-gold shrink-0" />
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
                <MapPin className="h-5 w-5 text-panashe-gold" />
                <span className="font-heading text-lg sm:text-xl font-bold">{destinationName || destination}</span>
              </div>
              <Badge variant="secondary" className="bg-panashe-gold text-slate-900 font-bold px-3 py-1 text-sm">
                <Calendar className="h-4 w-4 mr-1.5" />
                {searchedDateObj && format(searchedDateObj, 'EEE, dd MMMM yyyy')}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white px-3 py-1 text-sm">
                <Users className="h-4 w-4 mr-1.5" />
                {passengers} Passenger{passengers > 1 ? 's' : ''}
              </Badge>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="text-white border-white/40 hover:bg-white/20 font-semibold"
            >
              Modify Search
            </Button>
          </div>
        </div>
      </div>

      {/* Date Navigation Strip */}
      <div className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Prev Day Button */}
            <Button
              variant="outline"
              size="sm"
              disabled={!canGoPrevious}
              onClick={() => {
                if (canGoPrevious) {
                  const prevDate = subDays(searchedDateObj, 1);
                  handleDateChange(format(prevDate, 'yyyy-MM-dd'));
                }
              }}
              className="h-9 px-2.5 text-slate-700 disabled:opacity-40"
              title="Previous Day"
            >
              <ChevronLeft className="h-4 w-4 mr-0.5" />
              <span className="hidden sm:inline">Prev Day</span>
            </Button>

            {/* Quick Today Tab */}
            <Button
              variant={isSearchedDateToday && selectedTab === 'selected' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleDateChange(todayDateStr)}
              className={`h-9 font-medium ${
                isSearchedDateToday && selectedTab === 'selected'
                  ? 'bg-panashe-blue text-white shadow'
                  : 'bg-white hover:bg-slate-100 text-slate-700'
              }`}
            >
              🟢 Today ({format(new Date(), 'dd MMM')})
            </Button>

            {/* Quick Tomorrow Tab */}
            <Button
              variant={isSearchedDateTomorrow || selectedTab === 'tomorrow' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (searchedDateParam === tomorrowDateStr) {
                  setSelectedTab('selected');
                } else {
                  handleDateChange(tomorrowDateStr);
                }
              }}
              className={`h-9 font-medium ${
                isSearchedDateTomorrow || selectedTab === 'tomorrow'
                  ? 'bg-blue-600 text-white shadow'
                  : 'bg-white hover:bg-slate-100 text-slate-700'
              }`}
            >
              🔵 Tomorrow ({format(tomorrowDateObj, 'dd MMM')})
              {tripsTomorrow.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.2 bg-white/30 rounded-full text-xs font-bold">
                  {tripsTomorrow.length}
                </span>
              )}
            </Button>

            {/* View All Scheduled Trips Tab */}
            {enrichedTrips.length > 1 && (
              <Button
                variant={selectedTab === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedTab('all')}
                className={`h-9 font-medium ${
                  selectedTab === 'all'
                    ? 'bg-slate-800 text-white shadow'
                    : 'bg-white hover:bg-slate-100 text-slate-700'
                }`}
              >
                All Available Dates ({enrichedTrips.length})
              </Button>
            )}

            {/* Next Day Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const nextDate = addDays(searchedDateObj, 1);
                handleDateChange(format(nextDate, 'yyyy-MM-dd'));
              }}
              className="h-9 px-2.5 text-slate-700"
              title="Next Day"
            >
              <span className="hidden sm:inline">Next Day</span>
              <ChevronRight className="h-4 w-4 ml-0.5" />
            </Button>

            {/* Calendar Picker Popover */}
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 bg-white text-slate-700">
                  <Calendar className="h-4 w-4 mr-1 text-panashe-blue" />
                  Pick Date
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarPicker
                  mode="single"
                  selected={searchedDateObj}
                  onSelect={(d) => {
                    if (d) {
                      handleDateChange(format(d, 'yyyy-MM-dd'));
                      setCalendarOpen(false);
                    }
                  }}
                  disabled={(d) => {
                    const startToday = startOfDay(new Date());
                    return d < startToday;
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Currency Toggle */}
          <div className="flex items-center gap-1 bg-white border rounded-lg p-0.5">
            <Button
              variant={currency === 'USD' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setCurrency('USD')}
              className={`h-7 px-3 text-xs font-bold ${currency === 'USD' ? 'bg-panashe-blue text-white' : 'text-slate-600'}`}
              data-testid="currency-usd"
            >
              USD ($)
            </Button>
            <Button
              variant={currency === 'ZAR' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setCurrency('ZAR')}
              className={`h-7 px-3 text-xs font-bold ${currency === 'ZAR' ? 'bg-panashe-blue text-white' : 'text-slate-600'}`}
              data-testid="currency-zar"
            >
              ZAR (R)
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Helper Banner for Tomorrow's Trips if Available */}
        {!loading && tripsTomorrow.length > 0 && searchedDateParam !== tomorrowDateStr && selectedTab !== 'tomorrow' && (
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0 text-blue-700">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-blue-950">
                  Planning ahead? {tripsTomorrow.length} scheduled trip{tripsTomorrow.length > 1 ? 's are' : ' is'} available for Tomorrow ({format(tomorrowDateObj, 'EEEE, dd MMMM yyyy')})!
                </p>
                <p className="text-xs text-blue-700">
                  You can book today or pre-book tomorrow's departure right now.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold shrink-0 shadow-sm"
              onClick={() => handleDateChange(tomorrowDateStr)}
            >
              View Tomorrow's Trips ({tripsTomorrow.length})
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Results Title */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                {loading
                  ? 'Searching Available Trips...'
                  : selectedTab === 'all'
                  ? `All Available Trips (${enrichedTrips.length} Found)`
                  : selectedTab === 'tomorrow'
                  ? `Trips for Tomorrow (${format(tomorrowDateObj, 'EEEE, dd MMMM yyyy')})`
                  : `Trips for ${format(searchedDateObj, 'EEEE, dd MMMM yyyy')}`}
              </h2>
              <p className="text-sm text-slate-600 mt-0.5">
                Each trip below clearly shows its exact departure date and arrival time.
              </p>
            </div>
            {!loading && displayedTrips.length > 0 && (
              <Badge className="bg-slate-100 text-slate-800 text-sm px-3 py-1 border border-slate-300 font-bold">
                {displayedTrips.length} Trip{displayedTrips.length !== 1 ? 's' : ''} Displayed
              </Badge>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="card-panashe">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    <Skeleton className="h-28 w-full md:w-56" />
                    <div className="flex-1 space-y-3">
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-1/4" />
                    </div>
                    <Skeleton className="h-14 w-36" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && displayedTrips.length === 0 && (
          <Card className="card-panashe p-12 text-center border-2 border-dashed">
            <AlertCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">
              No Trips Scheduled for {format(searchedDateObj, 'EEEE, dd MMMM yyyy')}
            </h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              We couldn't find scheduled trips for this exact date. Try checking tomorrow or picking another travel date.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                onClick={() => handleDateChange(tomorrowDateStr)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
              >
                Check Tomorrow ({format(tomorrowDateObj, 'dd MMM')})
              </Button>
              {enrichedTrips.length > 0 && (
                <Button
                  onClick={() => setSelectedTab('all')}
                  variant="outline"
                  className="font-bold"
                >
                  View All Other Available Trips ({enrichedTrips.length})
                </Button>
              )}
              <Button onClick={() => navigate('/')} variant="ghost">
                Back to Search
              </Button>
            </div>
          </Card>
        )}

        {/* Trip Results List */}
        {!loading && displayedTrips.length > 0 && (
          <div className="space-y-6">
            {displayedTrips.map((trip) => {
              const { dateInfo } = trip;
              const isTodayTrip = dateInfo.isToday;
              const isTomorrowTrip = dateInfo.isTomorrow;

              return (
                <Card 
                  key={trip.id} 
                  className="card-panashe overflow-hidden hover:shadow-xl transition-all border-2 border-slate-200"
                  data-testid={`trip-card-${trip.id}`}
                >
                  {/* Top Departure Date Header Banner */}
                  <div className={`px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-2 border-b ${
                    isTodayTrip 
                      ? 'bg-emerald-600 text-white' 
                      : isTomorrowTrip 
                      ? 'bg-blue-700 text-white' 
                      : 'bg-slate-800 text-white'
                  }`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Calendar className="h-4 w-4" />
                      <span className="text-xs uppercase font-bold tracking-wider opacity-90">
                        DEPARTURE DATE:
                      </span>
                      <span className="font-extrabold text-sm sm:text-base underline decoration-white/40">
                        {dateInfo.depDateFull}
                      </span>
                      {isTodayTrip && (
                        <span className="bg-white text-emerald-800 text-xs px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider shadow-sm">
                          ● TODAY'S TRIP
                        </span>
                      )}
                      {isTomorrowTrip && (
                        <span className="bg-white text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider shadow-sm">
                          ★ TOMORROW'S TRIP
                        </span>
                      )}
                    </div>

                    <div className="text-xs font-semibold opacity-90">
                      Route: <span className="font-bold">{trip.route_name}</span> • Bus: <span className="font-bold">{trip.bus}</span>
                    </div>
                  </div>

                  <CardContent className="p-0">
                    <div className="flex flex-col lg:flex-row">
                      {/* Main Trip Details */}
                      <div className="flex-1 p-5 sm:p-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-center">
                          {/* Departure Point */}
                          <div className="text-center sm:text-left bg-slate-50 sm:bg-transparent p-3 sm:p-0 rounded-lg border sm:border-0">
                            <div className="inline-flex items-center gap-1.5 text-xs uppercase font-extrabold text-panashe-blue mb-1">
                              <Clock className="h-3.5 w-3.5" />
                              DEPARTS
                            </div>
                            <p className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 leading-none">
                              {dateInfo.depTime}
                            </p>
                            <p className="text-sm font-bold text-panashe-blue mt-1">
                              {dateInfo.depDateShort}
                            </p>
                            <p className="text-base font-bold text-slate-800 mt-1 flex items-center justify-center sm:justify-start gap-1">
                              <MapPin className="h-4 w-4 text-panashe-red shrink-0" />
                              {trip.origin}
                            </p>
                            <p className="text-xs text-slate-500">Boarding Station</p>
                          </div>
                          
                          {/* Journey Duration & Path */}
                          <div className="flex flex-col items-center justify-center px-2">
                            <span className="text-xs font-bold text-slate-700 bg-slate-100 px-3 py-1 rounded-full border border-slate-300 shadow-sm mb-2">
                              ⏱️ {trip.duration_hours || 22}h Journey
                            </span>
                            <div className="w-full flex items-center gap-2">
                              <div className="h-3 w-3 rounded-full bg-panashe-blue shadow" />
                              <div className="flex-1 border-t-2 border-dashed border-slate-300 relative" />
                              <div className="h-3 w-3 rounded-full bg-panashe-gold shadow" />
                            </div>
                            {dateInfo.dayDiff > 0 && (
                              <div className="mt-2 text-center">
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-900 bg-amber-100 border border-amber-300 px-2.5 py-0.5 rounded-md shadow-sm">
                                  <Moon className="h-3 w-3 text-amber-700" />
                                  Arrives Next Day (+{dateInfo.dayDiff} Day{dateInfo.dayDiff > 1 ? 's' : ''})
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Arrival Point */}
                          <div className="text-center sm:text-right bg-slate-50 sm:bg-transparent p-3 sm:p-0 rounded-lg border sm:border-0">
                            <div className="inline-flex items-center justify-center sm:justify-end gap-1.5 text-xs uppercase font-extrabold text-emerald-700 mb-1">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              ARRIVES
                            </div>
                            <p className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 leading-none">
                              {dateInfo.arrTime || '--:--'}
                            </p>
                            <p className="text-sm font-bold text-emerald-700 mt-1">
                              {dateInfo.arrDateShort || 'Next Day'}
                              {dateInfo.dayDiff > 0 && <span className="text-xs ml-1 text-amber-700 font-extrabold">(+1)</span>}
                            </p>
                            <p className="text-base font-bold text-slate-800 mt-1 flex items-center justify-center sm:justify-end gap-1">
                              <MapPin className="h-4 w-4 text-emerald-600 shrink-0" />
                              {trip.destination}
                            </p>
                            <p className="text-xs text-slate-500">Drop-off Destination</p>
                          </div>
                        </div>

                        {/* Schedule Confirmation Strip */}
                        <div className="mt-4 pt-3 border-t border-slate-200 bg-slate-50/80 rounded-lg p-2.5 flex flex-wrap items-center justify-between gap-2 text-xs">
                          <div className="flex items-center gap-1.5 text-slate-700 font-medium">
                            <Info className="h-4 w-4 text-panashe-blue shrink-0" />
                            <span>
                              <strong>Schedule Confirmation:</strong> Departs <strong>{dateInfo.depDateShort} at {dateInfo.depTime}</strong> ➔ Arrives <strong>{dateInfo.arrDateShort} at {dateInfo.arrTime}</strong>
                            </span>
                          </div>
                          {trip.via && (
                            <span className="text-slate-500 text-[11px]">Via: {trip.via}</span>
                          )}
                        </div>

                        {/* Amenities & Seats Left */}
                        <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-100">
                          <div className="flex flex-wrap items-center gap-2">
                            {trip.amenities?.map((amenity, idx) => {
                              const Icon = getAmenityIcon(amenity);
                              return Icon ? (
                                <Badge key={idx} variant="outline" className="text-slate-600 border-slate-300 bg-white">
                                  <Icon className="h-3 w-3 mr-1 text-panashe-blue" />
                                  {amenity}
                                </Badge>
                              ) : null;
                            })}
                          </div>
                          
                          <Badge 
                            variant={trip.available_seats > 10 ? 'default' : 'destructive'}
                            className={`font-bold px-3 py-1 ${trip.available_seats > 10 ? 'bg-green-100 text-green-800 border border-green-300' : ''}`}
                          >
                            <Users className="h-3.5 w-3.5 mr-1" />
                            {trip.available_seats} seats remaining
                          </Badge>
                        </div>
                      </div>

                      {/* Pricing & Booking Column */}
                      <div className="bg-gradient-to-b from-slate-50 to-slate-100 p-6 flex flex-col justify-center items-center min-w-[220px] border-t lg:border-t-0 lg:border-l border-slate-200">
                        <p className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-0.5">Fare Price</p>
                        <p className="font-heading text-4xl font-black text-panashe-blue mb-0.5">
                          {currency === 'USD' ? '$' : 'R'}
                          {currency === 'USD' ? trip.price_usd : trip.price_zar}
                        </p>
                        <p className="text-xs text-slate-500 mb-4 font-medium">per person</p>
                        
                        <Button 
                          className="btn-panashe w-full font-bold shadow-md hover:shadow-lg text-sm h-11"
                          onClick={() => trip?.id && handleSelectTrip(trip.id)}
                          disabled={trip.available_seats < passengers}
                          data-testid={`select-trip-${trip?.id}`}
                        >
                          Select Seats
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>

                        <p className="text-[11px] font-semibold text-slate-600 mt-2 text-center">
                          📅 For departure on <strong>{dateInfo.depDateShort}</strong>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default SearchResultsPage;
